/**
 * Keep keyboard focus inside an open overlay and hand it back on close.
 *
 * On activation focus moves to `initialFocus` (a ref, or the first focusable
 * element, or the container itself). Tab and Shift+Tab cycle inside the
 * container while `isTop()` says this overlay is the top layer — a confirm
 * dialog on top of a drawer owns Tab, the drawer does not. On deactivation
 * focus returns to whatever held it before, if that element is still in the
 * document.
 */
import { useEffect, type RefObject } from "react";

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled]):not([type=hidden])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex='-1'])",
  "[contenteditable='true']",
].join(",");

export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute("inert") && el.getClientRects().length > 0,
  );
}

interface Options {
  initialFocus?: RefObject<HTMLElement | null> | "first" | "container";
  returnFocus?: boolean;
  isTop?: () => boolean;
}

export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  { initialFocus = "first", returnFocus = true, isTop }: Options = {},
): void {
  useEffect(() => {
    const container = containerRef.current;
    if (!active || !container) return;
    const previous = document.activeElement as HTMLElement | null;

    const focusInitial = () => {
      if (container.contains(document.activeElement)) return;
      let target: HTMLElement | null = null;
      if (initialFocus === "container") target = container;
      else if (initialFocus === "first") target = focusableWithin(container)[0] ?? container;
      else target = initialFocus.current ?? focusableWithin(container)[0] ?? container;
      if (target === container && !container.hasAttribute("tabindex")) {
        container.setAttribute("tabindex", "-1");
      }
      target.focus({ preventScroll: true });
    };
    // After paint, so autoFocus inside the panel and portal mounting settle first.
    const raf = window.requestAnimationFrame(focusInitial);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "Tab" || (isTop && !isTop())) return;
      const items = focusableWithin(container);
      if (items.length === 0) {
        e.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      if (e.shiftKey && (current === first || !container.contains(current))) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (current === last || !container.contains(current))) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);

    return () => {
      window.cancelAnimationFrame(raf);
      document.removeEventListener("keydown", onKeyDown);
      if (returnFocus && previous && document.contains(previous)) {
        previous.focus({ preventScroll: true });
      }
    };
    // initialFocus/isTop are read at activation; changing them mid-open is not supported.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, containerRef]);
}
